import { useCallback, useEffect, useRef, useState } from "react";

const DISMISSED_KEY = "incassaflow_install_dismissed";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Capture the event globally so it's not lost before React mounts
let _deferredPrompt: BeforeInstallPromptEvent | null = null;
window.addEventListener("beforeinstallprompt", (e: Event) => {
  e.preventDefault();
  _deferredPrompt = e as BeforeInstallPromptEvent;
});

export function useInstallPrompt() {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(_deferredPrompt);
  const [canInstall, setCanInstall] = useState(
    () => _deferredPrompt !== null && localStorage.getItem(DISMISSED_KEY) !== "true"
  );
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === "true");

  useEffect(() => {
    function handleBeforeInstall(e: Event) {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      _deferredPrompt = evt;
      deferredPrompt.current = evt;
      if (!dismissed) {
        setCanInstall(true);
      }
    }

    function handleAppInstalled() {
      _deferredPrompt = null;
      deferredPrompt.current = null;
      setCanInstall(false);
    }

    // Pick up event captured before mount
    if (_deferredPrompt && !dismissed) {
      deferredPrompt.current = _deferredPrompt;
      setCanInstall(true);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [dismissed]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt.current) return;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === "accepted") {
      setCanInstall(false);
    }
    _deferredPrompt = null;
    deferredPrompt.current = null;
  }, []);

  const dismiss = useCallback(() => {
    setCanInstall(false);
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, "true");
  }, []);

  return { canInstall: canInstall && !dismissed, promptInstall, dismiss };
}
